import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { KeyRound, LogOut, User, Mic, Headphones, Languages, Calculator, FileJson, ClipboardCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import Brand from "./Brand";
import ThemeToggle from "./ThemeToggle";
import ChangePasswordDialog from "./ChangePasswordDialog";
import VoiceSettings from "./VoiceSettings";
import MeetingMode from "./MeetingMode";
import LanguageSettings from "./LanguageSettings";
import TemplateLibrary from "./TemplateLibrary";
import MicButton from "./MicButton";
import { useWakeWord, useVoicePrefs, refreshVoicePrefsFromServer } from "../lib/voice";
import { useT, refreshLanguageFromServer } from "../lib/i18n";
import { toast } from "sonner";

export default function AppShell({ children, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const t = useT();
  const [menu, setMenu] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [micFlash, setMicFlash] = useState(false);
  const prefs = useVoicePrefs();

  // Refresh voice + language prefs from server once when shell mounts.
  useEffect(() => {
    if (!user) return;
    refreshVoicePrefsFromServer();
    refreshLanguageFromServer();
  }, [user]);

  // Continuous wake-word ("Hey Capexara Process Industry") — when enabled in settings,
  // listen globally and flash the floating mic to invite the user to speak.
  useWakeWord({
    enabled: !!user && prefs?.mode === "continuous",
    wakeWord: prefs?.wake_word || "Hey Capexara Process Industry",
    onWake: () => {
      setMicFlash(true);
      try { toast.info?.(`👂 Heard "${prefs?.wake_word || "Hey Capexara Process Industry"}" — listening…`); }
      catch (_) { /* ok */ }
      setTimeout(() => setMicFlash(false), 4000);
      // Programmatically click the floating mic so recording starts.
      try {
        const btn = document.querySelector('[data-testid="floating-mic"]');
        if (btn) btn.click();
      } catch (_) { /* ok */ }
    },
  });

  // Floating-mic transcript handler — insert text into the currently
  // focused input/textarea, fall back to clipboard, and trigger submit
  // when the user said an explicit submit keyword (auto_submit=true).
  const handleFloatingTranscript = (text, meta) => {
    const el = document.activeElement;
    const isInput = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA"
                                  || el.isContentEditable);
    if (isInput) {
      try {
        const cur = el.value || "";
        const next = cur ? cur + " " + text : text;
        const setter = Object.getOwnPropertyDescriptor(
            el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype
                                                 : window.HTMLInputElement.prototype,
            "value")?.set;
        if (setter) { setter.call(el, next); }
        else { el.value = next; }
        el.dispatchEvent(new Event("input", { bubbles: true }));
      } catch (_) { /* fall back below */ }
    } else {
      try { navigator.clipboard?.writeText(text); } catch (_) {}
      try { toast.success?.(`Voice copied: ${text.slice(0, 60)}…`); } catch (_) {}
    }
  };

  const handleFloatingAutoSubmit = (text, meta) => {
    const el = document.activeElement;
    if (!el) return;
    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA"
            || el.isContentEditable) {
      // Fire Enter to submit forms / trigger keydown handlers
      try {
        el.dispatchEvent(new KeyboardEvent("keydown",
            { key: "Enter", code: "Enter", keyCode: 13, which: 13,
              bubbles: true, cancelable: true }));
      } catch (_) { /* ok */ }
      // If still nothing happens, look for the nearest primary submit button
      const form = el.closest("form");
      if (form) {
        const submit = form.querySelector('button[type="submit"], button.btn-primary');
        if (submit) submit.click();
      }
    }
    try { toast.success?.(`Auto-submitted: "${text.slice(0, 50)}…"`); } catch (_) {}
  };

  return (
    <div className="pf-grain" style={{ minHeight: "100vh", background: "var(--pf-bg)", display: "flex", flexDirection: "column" }}>
      <header style={{ borderBottom: "1px solid var(--pf-border)", position: "sticky", top: 0, background: "rgba(10,10,10,0.88)", backdropFilter: "blur(8px)", zIndex: 30 }}>
        <div style={{ padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Link to="/projects" className="pf-heading" style={{ display: "flex", alignItems: "center", color: "var(--pf-text)", textDecoration: "none" }} data-testid="shell-brand">
              <Brand height={48} testId="shell-brand-inner" />
            </Link>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {actions}
            {/* Design Review — global entry point (RG10). Visible whenever
                a user is signed in; routes to the RE project picker. */}
            {user && (
              <Link to="/design-review" className="btn btn-ghost"
                  style={{ fontSize: 12, padding: "4px 10px",
                                color: "#22D3EE", textDecoration: "none" }}
                  title="Review Existing Design (Real Estate)"
                  data-testid="shell-design-review-link">
                <ClipboardCheck size={14} /> Design Review
              </Link>
            )}
            {/* Spec §LOCATION — top toolbar "Cost" icon (always visible on
                a project context) — quick jump to Cost Estimation panel. */}
            {(() => {
              const m = loc.pathname.match(/\/projects\/([^/]+)/);
              if (!m) return null;
              const target = `/projects/${m[1]}/engineering/cost-engineering/cost-estimation`;
              return (
                <Link to={target} className="btn btn-ghost"
                    style={{ fontSize: 12, padding: "4px 10px",
                                  color: "#F26B1F", textDecoration: "none" }}
                    title="Cost estimate"
                    data-testid="shell-cost-link">
                  <Calculator size={14} /> Cost
                </Link>
              );
            })()}
            <ThemeToggle testId="shell-theme-toggle" />
            <div style={{ position: "relative" }}>
              <button className="btn btn-ghost" onClick={() => setMenu((v) => !v)} data-testid="shell-user-menu">
                <User size={14}/> {user?.name || user?.email || "Account"}
              </button>
              {menu && (
                <div className="pf-card" style={{ position: "absolute", right: 0, top: "110%", minWidth: 200, padding: 8, zIndex: 40 }}>
                  <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--pf-text-muted)", fontFamily: "JetBrains Mono" }}>
                    {user?.email}
                  </div>
                  <div className="pf-divider" style={{ margin: "4px 0" }}/>
                  <button
                    className="btn btn-ghost"
                    style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent" }}
                    onClick={() => { setMenu(false); setPwdOpen(true); }}
                    data-testid="shell-change-password"
                  >
                    <KeyRound size={14}/> {t("change_password")}
                  </button>
                  <button className="btn btn-ghost"
                      style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent" }}
                      onClick={() => { setMenu(false); setLangOpen(true); }}
                      data-testid="shell-language">
                    <Languages size={14}/> {t("language")}
                  </button>
                  <button className="btn btn-ghost"
                      style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent" }}
                      onClick={() => { setMenu(false); setVoiceOpen(true); }}
                      data-testid="shell-voice-settings">
                    <Mic size={14}/> {t("voice_settings")}
                  </button>
                  <button className="btn btn-ghost"
                      style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent" }}
                      onClick={() => { setMenu(false); setMeetingOpen(true); }}
                      data-testid="shell-meeting-mode">
                    <Headphones size={14}/> {t("meeting_mode")}
                  </button>
                  <button className="btn btn-ghost"
                      style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent" }}
                      onClick={() => { setMenu(false); setTplOpen(true); }}
                      data-testid="shell-template-library">
                    <FileJson size={14}/> Template Library
                  </button>
                  <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "flex-start", borderColor: "transparent" }} onClick={async () => { await logout(); nav("/"); }} data-testid="shell-logout">
                    <LogOut size={14}/> {t("sign_out")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <main style={{ flex: 1, display: "flex", flexDirection: "column" }}>{children}</main>
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <VoiceSettings open={voiceOpen} onClose={() => setVoiceOpen(false)} />
      <LanguageSettings open={langOpen} onClose={() => setLangOpen(false)} />
      <TemplateLibrary open={tplOpen} onClose={() => setTplOpen(false)} />
      <MeetingMode open={meetingOpen}
            onClose={() => setMeetingOpen(false)}
            onApplyItem={(text) => {
              try { navigator.clipboard?.writeText(text); } catch (_) { /* ok */ }
              toast?.success?.(`Action copied: ${text.slice(0, 60)}…`);
            }} />
      {/* Floating mic — visible on mobile/tablet (≤1024px), or whenever
          continuous wake-word mode is active so a "Hey Capexara Process Industry" produces
          something visible to click. */}
      {user && (
        <div className="rachana-floating-mic"
             style={{
               position: "fixed", bottom: 24, right: 24, zIndex: 998,
               // Highlight ring when we just heard the wake-word
               boxShadow: micFlash
                   ? "0 0 0 6px rgba(242,107,31,0.45)" : "none",
               borderRadius: "50%",
               transition: "box-shadow 0.3s",
             }}>
          <style>{`
            @media (min-width: 1025px) {
              .rachana-floating-mic { display: ${prefs?.mode === "continuous" ? "block" : "none"}; }
            }
          `}</style>
          <MicButton testId="floating-mic" size="lg" variant="inline"
              pushToTalkSpacebar={false}
              onTranscript={handleFloatingTranscript}
              onAutoSubmit={handleFloatingAutoSubmit} />
        </div>
      )}
    </div>
  );
}
